import { Injectable } from "@angular/core";
import { BehaviorSubject, combineLatest, Observable, of } from "rxjs";
import { first, map, shareReplay, switchMap } from "rxjs/operators";
import { APIService, KPI } from "../api";
import * as R from "ramda";
import { TranslateService } from "@ngx-translate/core";
import { SpreadsheetColumnsInvestmentsService } from "./spreadsheet-columns-investments.service";
import { SpreadsheetColumnsAssetsService } from "./spreadsheet-columns-assets.service";
import { YearsService } from "./years.service";
import { ActivatedRoute } from "@angular/router";
import { FilterColumnToModel, ObservableMap, operatorsMap, reversedOperatorsMap, TableFilterColumns } from "./spreadsheet-columns.service";
import { InvestmentScenariiService } from "./investment-scenarii.service";
import { DEFAULT_ASSET_FILTERS, DEFAULT_INVESTMENT_FILTERS, DEFAULT_INITIATIVE_FILTERS } from "./constants";
import { SpreadsheetColumnsInitiativesService } from './spreadsheet-columns-initiatives.service';
import { PerimetersService } from './perimeters.service';
import { SpreadsheetSitesService } from './spreadsheet-sites.service';

export interface BehaviorSubjectMap<T> {
    [key: string]: BehaviorSubject<T>;
}

@Injectable()
export class QuickFiltersService {

    constructor(
        private translate: TranslateService,
        private spreadsheetColumnsInvestmentsService: SpreadsheetColumnsInvestmentsService,
        private spreadsheetColumnsAssetsService: SpreadsheetColumnsAssetsService,
        private spreadsheetColumnsInitiativesService: SpreadsheetColumnsInitiativesService,
        private spreadsheetSitesService: SpreadsheetSitesService,
        private yearsService: YearsService,
        private route: ActivatedRoute,
        private investmentScenariiService: InvestmentScenariiService,
        private api: APIService,
        private perimetersService: PerimetersService,
    ) { }

    private booleanFilterOptions: KPI.AgGridBooleanFilterOption[] = [
        { name: this.translate.instant('values.true'), value: true },
        { name: this.translate.instant('values.false'), value: false },
    ]

    public booleanToNumberFilterOptions = [
        { name: this.translate.instant('values.true'), value: "> 0" },
        { name: this.translate.instant('values.false'), value: "<= 0" }
    ]

    public otherFilterOptions(): Observable<{
        [spreadsheetType: string]: { [field: string]: { name: string, value: (string | number | boolean) }[] }
    }> {
        return this.perimetersService.perimeters$.pipe(
            map((perimeters) => ({
                asset: {
                    technical_state_notation: [
                        { name: this.translate.instant('asset.no_notation'), value: -999 },
                        { name: this.translate.instant('core.utils.state-levels.critical'), value: 0 },
                        { name: this.translate.instant('core.utils.state-levels.deteriorated'), value: 1 },
                        { name: this.translate.instant('core.utils.state-levels.acceptable'), value: 2 },
                        { name: this.translate.instant('core.utils.state-levels.good'), value: 3 },
                    ],
                    remaining_lifetime_without_impact: [
                        { name: "= 1 " + this.translate.instant('years'), value: "== 1" },
                        { name: "<= 2 " + this.translate.instant('years'), value: "<= 2" },
                        { name: "<= 3 " + this.translate.instant('years'), value: "<= 3" },
                        { name: "<= 5 " + this.translate.instant('years'), value: "<= 5" },
                    ],
                    planned_investment_number: [
                        { name: this.translate.instant('Yes'), value: "> 0" },
                        { name: this.translate.instant('No'), value: "< 1" },
                    ],
                    has_initiatives: this.booleanFilterOptions,
                    //   defaults: [
                    //     { name: "= 0 ", value: "== 0" },
                    //     { name: "= 1 ", value: "== 1" },
                    //     { name: "= 2 ", value: "== 2" },
                    //     { name: "= 3 ", value: "== 3" },
                    //     { name: "= 4 ", value: "== 4" },
                    //     { name: "= 5 ", value: "== 5" },
                    //     { name: "= 6 ", value: "== 6" },
                    // ],
                },
                investment: {
                    has_initiatives: this.booleanFilterOptions,
                    has_invoicing: this.booleanFilterOptions,
                    has_taxonomy_objective: this.booleanFilterOptions,
                    priority: [
                      { name: this.translate.instant('High'), value: 1 },
                      { name: this.translate.instant('Medium'), value: 2 },
                      { name: this.translate.instant('Low'), value: 3 },
                      { name: this.translate.instant('Urgent'), value: 4 },
                    ],
                    site: [
                      ...new Set(
                        perimeters
                          .sort((a, b) => a.name.localeCompare(b.name))
                          .map((p) => ({
                            name: p.name,
                            value: p.id,
                          })),
                      ),
                    ],
                },
                initiative: {},
                site: {},
            })),
        );
    }
    private defaultFilterStorageKeys: { [spreadsheetType: string]: string } = {
        asset: DEFAULT_ASSET_FILTERS,
        investment: DEFAULT_INVESTMENT_FILTERS,
        initiative: DEFAULT_INITIATIVE_FILTERS,
        site: DEFAULT_ASSET_FILTERS,
    };
    public filters: { [spreadsheetType: string]: { [field: string]: any } } = {
        asset: {},
        investment: {},
        initiative: {},
        site: {},
    };
    public filtersUpdated = {
        asset: new BehaviorSubject<void>(undefined),
        investment: new BehaviorSubject<void>(undefined),
        initiative: new BehaviorSubject<void>(undefined),
        site: new BehaviorSubject<void>(undefined),
    };
    public filtersUpdatedFromSpreadsheet = {
        asset: new BehaviorSubject<void>(undefined),
        investment: new BehaviorSubject<void>(undefined),
        initiative: new BehaviorSubject<void>(undefined),
        site: new BehaviorSubject<void>(undefined),
    };
    private ensureBehaviorSubjects(spreadsheetType: string): void {
        if (!this.filters[spreadsheetType]) {
            this.filters[spreadsheetType] = {};
        }
        if (!this.filtersUpdated[spreadsheetType]) {
            this.filtersUpdated[spreadsheetType] = new BehaviorSubject<void>(undefined);
        }
        if (!this.filtersUpdatedFromSpreadsheet[spreadsheetType]) {
            this.filtersUpdatedFromSpreadsheet[spreadsheetType] = new BehaviorSubject<void>(undefined);
        }
        if (!this.currentFilterModels[spreadsheetType]) {
            this.currentFilterModels[spreadsheetType] = new BehaviorSubject<any>({});
        }
    }

    public setFilter(spreadsheetType: KPI.SpreadsheetType, field: string, values: (string | number | boolean)[]): void {
        this.ensureBehaviorSubjects(spreadsheetType);
        if (!this.filters[spreadsheetType][field] || !R.equals(this.filters[spreadsheetType][field], values)) {
            this.filters[spreadsheetType][field] = values;
            this.filtersUpdated[spreadsheetType].next();
        }
    }

    public removeFilter(spreadsheetType: KPI.SpreadsheetType, field: string): void {
        this.ensureBehaviorSubjects(spreadsheetType);
        if (typeof typeof this.filters[spreadsheetType][field] === 'string') {
            this.filters[spreadsheetType][field] = '';
        } else {
            this.filters[spreadsheetType][field] = [];
        }
        this.filtersUpdated[spreadsheetType].next();
    }

    // Set default filters state to 'removed' to prevent them from being set again
    // Don't provide a column to remove all default filters
    public removeDefaultFilter(spreadsheetType: KPI.SpreadsheetType, column?: string): void {
        const defaultFiltersStorageKey = this.defaultFilterStorageKeys[spreadsheetType];
        const defaultFiltersState = JSON.parse(localStorage.getItem(defaultFiltersStorageKey));
        if (column && defaultFiltersState && defaultFiltersState[column]) {
            localStorage.setItem(defaultFiltersStorageKey, JSON.stringify({
                ...defaultFiltersState,
                [column]: 'removed',
            }));
        } else if (!column && defaultFiltersState) {
            const newDefaultFiltersState = {}
            Object.keys(defaultFiltersState).map((column) => newDefaultFiltersState[column] = 'removed');
            localStorage.setItem(defaultFiltersStorageKey, JSON.stringify(newDefaultFiltersState));
        }
    }

    public getFilter(
        spreadsheetType: KPI.SpreadsheetType,
        field: string,
        ignoreSpreadsheet?: boolean,
        ignoreQuickFilter?: boolean
    ): Observable<any> {
        this.ensureBehaviorSubjects(spreadsheetType);
        if (ignoreSpreadsheet) {
            return this.filtersUpdated[spreadsheetType].pipe(
                switchMap(() => of(this.filters[spreadsheetType]?.[field])),
            );
        } else if (ignoreQuickFilter) {
            return this.filtersUpdatedFromSpreadsheet[spreadsheetType].pipe(
                switchMap(() => of(this.filters[spreadsheetType]?.[field])),
            );
        } else {
            return combineLatest([
                this.filtersUpdatedFromSpreadsheet[spreadsheetType],
                this.filtersUpdated[spreadsheetType],
            ]).pipe(
                switchMap(() => of(this.filters[spreadsheetType]?.[field])),
            );
        }
    }

    public getFilters(
        spreadsheetType: KPI.SpreadsheetType,
        ignoreSpreadsheet?: boolean,
        ignoreQuickFilter?: boolean
    ): Observable<any> {
        this.ensureBehaviorSubjects(spreadsheetType);
        if (ignoreSpreadsheet) {
            return this.filtersUpdated[spreadsheetType].pipe(
                switchMap(() => of(this.filters[spreadsheetType] || {})),
            );
        } else if (ignoreQuickFilter) {
            return this.filtersUpdatedFromSpreadsheet[spreadsheetType].pipe(
                switchMap(() => of(this.filters[spreadsheetType] || {})),
            );
        } else {
            return combineLatest([
                this.filtersUpdatedFromSpreadsheet[spreadsheetType],
                this.filtersUpdated[spreadsheetType],
            ]).pipe(
                switchMap(() => of(this.filters[spreadsheetType] || {})),
            );
        }
    }

    public currentFilterModels: { [spreadsheetType: string]: BehaviorSubject<any> } = {
        asset: new BehaviorSubject<any>({}),
        investment: new BehaviorSubject<any>({}),
        initiative: new BehaviorSubject<any>({}),
        site: new BehaviorSubject<any>({}),
    };

    public setcurrentFilterModel(spreadsheetType: KPI.SpreadsheetType, filterModel: any): void {
        this.ensureBehaviorSubjects(spreadsheetType);
        this.currentFilterModels[spreadsheetType].next(filterModel);
    }

    public setFiltersFromSpreadsheet(spreadsheetType: KPI.SpreadsheetType, filterModel: any): void {
      this.ensureBehaviorSubjects(spreadsheetType);
      const baseType = spreadsheetType.split('_')[0] as KPI.SpreadsheetType;
      this.getQuickFilters(baseType, true).pipe(first()).subscribe((quickFilters) => {
        this.filters[spreadsheetType] = {};
        Object.keys(filterModel).filter((field) =>
          quickFilters.map((column) => column.field).includes(field as TableFilterColumns)
        ).map((field) => {
          const filterType = filterModel[field].filterType;
          const currentFilterValues = this.filters[spreadsheetType][field];
          if (filterType === 'set') {
              const newFilterValues = filterModel[field].values;
              if (!currentFilterValues || !R.equals(currentFilterValues, newFilterValues)) {
                  this.filters[spreadsheetType][field] = newFilterValues;
              }
          } else if (filterType === 'number') {
              const longOperator = filterModel[field].type;
              const newFilterValue = reversedOperatorsMap[longOperator] + ' ' + filterModel[field].filter;
              if (!currentFilterValues || !R.equals(currentFilterValues, newFilterValue)) {
                  this.filters[spreadsheetType][field] = newFilterValue;
              }
          }
        });
        this.filtersUpdatedFromSpreadsheet[spreadsheetType].next();
      });
    }


    public getColumnsFromSpreadsheet(type: KPI.SpreadsheetType, showScenarii: boolean = false): Observable<(KPI.CustomColDef | KPI.CustomColGroupDef)[]> {
        // Retrieve columns from the requested spreadsheet
        let columnsObservable: Observable<(KPI.CustomColDef | KPI.CustomColGroupDef)[]>;
        if (type) {
            switch (type) {
                case 'investment':
                    columnsObservable = this.spreadsheetColumnsInvestmentsService.getAgGridInvestmentColumns(showScenarii);
                    break;
                case 'asset':
                    columnsObservable = combineLatest(
                        this.route.queryParams,
                        this.yearsService.startYear$,
                    ).pipe(
                        switchMap(([queryParams, startYear]) => {
                            let year: number = queryParams['year']
                                ? Number(queryParams['year'])
                                : startYear;
                            return this.spreadsheetColumnsAssetsService.getAgGridAssetColumns(year);
                        }));
                    break;
                case 'initiative':
                    columnsObservable = this.spreadsheetColumnsInitiativesService.getAgGridInitiativeColumns(showScenarii);
                    break;
                case 'site':
                    columnsObservable = this.spreadsheetSitesService.getAgGridSitesColumns(false);
                    break;
                default:
                    break;
            }
        } else {
            columnsObservable = of([]);
        }

        return columnsObservable;
    }

    private filterValues: ObservableMap<any> = {};
    /**
     *
     * @param colId representation of ag-grid column name
     * @param params contains the information of
     *  - buildings: List of perimeter/building we should filter
     *  - dependencies: all previous ag-grid filter objects
     *  - start_year | end_year | scenario we also consider as dependencies
     */
    public getAvailableFilterValues<T>(
        colId: TableFilterColumns,
        params: {
            buildings: number[],
            filterModel?: { [key: string]: any },
            start_year?: number,
            end_year?: number,
            year?: number,
            scenario?: number,
            table?: "Investment" | "Asset" | "Initiative"
        },
        options?: { includeSelfFilter?: boolean },
    ): Observable<T> {
        const key = this.getKey(arguments);
        const model = FilterColumnToModel[colId] || colId;
        const { filterModel } = params;
        if (!this.filterValues[key]) {
            this.filterValues[key] = combineLatest([
                this.yearsService.startYear$,
                this.yearsService.endYear$,
                this.investmentScenariiService.mainScenario$
            ]).pipe(
                map(([startYear, endYear, mainScenario]) => {
                    params = {
                        ...params,
                        start_year: startYear,
                        end_year: endYear,
                        filterModel: options?.includeSelfFilter
                            ? (filterModel || {})
                            : filterModel
                                ? {
                                    ...filterModel,
                                    [colId]: undefined,
                                }
                                : {},
                    }
                    params['scenario'] = params.scenario ? params.scenario : mainScenario.id;
                    return params
                }),
                switchMap(params => this.api.available_filter_values({ ...params, model })),
                shareReplay(1),
            )
        }

        return this.filterValues[key];
    }

    public buildNumberFilter(values: string): any {
        const parts = values.trim().split(' ');
        const operator = parts[0];
        const value = parts[1];
        
        if (!operator || !operatorsMap[operator] || !value) {
            return null;
        }
        
        if (operatorsMap[operator] === 'inRange') {
            const from = Number(value.split('-')[0]);
            const to = Number(value.split('-')[1]);
            if (!this.checkNumber(from) || !this.checkNumber(to)) {
                return null;
            }
            return {
                filter: from,
                filterTo: to,
                filterType: 'number',
                type: 'inRange',
            };
        }
        
        const numValue = Number(value);
        if (!this.checkNumber(numValue)) {
            return null;
        }
        
        return {
            filter: numValue,
            filterType: 'number',
            type: operatorsMap[operator],
        };
    }

    private checkNumber(value: any): boolean {
        return !isNaN(Number(value));
    }

    public getQuickFilters(spreadsheetType: KPI.SpreadsheetType, showScenarii: boolean): Observable<KPI.QuickFilter[]> {
        return this.getColumnsFromSpreadsheet(spreadsheetType, showScenarii).pipe(
            map((columns) => this.initializeQuickFilters(columns))
        );
    }

    public initializeQuickFilters(columns: (KPI.CustomColDef | KPI.CustomColGroupDef)[]): KPI.QuickFilter[] {
        const columnFilters: KPI.QuickFilter[] = [];
        columns.map((column) => {
            // @ts-ignore
            if (column.children) {
                // @ts-ignore
                column.children.map((child: KPI.CustomColDef) => {
                    if (child.quickFilter) {
                        columnFilters.push({
                          // @ts-ignore
                          field: child.field,
                          name: child.altName || child.headerName,
                          // @ts-ignore
                          refData: child.refData,
                          numberFilter: child.filter === 'agNumberColumnFilter' && !child.booleanToNumberFilter,
                          booleanToNumberFilter: child.booleanToNumberFilter,
                          // @ts-ignore
                          availableFilterField: child.availableFilterField,
                        });
                    }
                });
            } else {
                // @ts-ignore
                if (column.quickFilter) {
                    columnFilters.push({
                      // @ts-ignore
                      field: column.field,
                      name: column.altName || column.headerName,
                      // @ts-ignore
                      refData: column.refData,
                      numberFilter: column.filter === 'agNumberColumnFilter' && !column.booleanToNumberFilter,
                      booleanToNumberFilter: column.booleanToNumberFilter,
                      // @ts-ignore
                      availableFilterField: column.availableFilterField,
                    });
                }
            }
        });
        return columnFilters;
    }

    private getKey(args: IArguments): string {
        return JSON.stringify(R.pickBy(R.complement(R.isNil), args));
    }

}
